import React, { useRef } from "react";
import { format } from "date-fns";
import { TripX } from "@/types/trip";
import { useCalendarLogic } from "@/hooks/useCalendarLogic";
import { CalendarDay } from "./CalendarDay";

interface CalendarProps {
  startBlock: Date;
  endBlock: Date;
  selectedTrip: TripX | null;
  isLoading?: boolean;
  onNextBlock?: () => void;
  onPrevBlock?: () => void;
}

const Calendar: React.FC<CalendarProps> = ({
  startBlock,
  endBlock,
  selectedTrip,
  isLoading = false,
  onNextBlock,
  onPrevBlock,
}) => {
  const calendarRef = useRef<HTMLDivElement>(null);
  const { days, calendarItems } = useCalendarLogic(
    startBlock,
    endBlock,
    selectedTrip
  );

  if (isLoading) {
    return (
      <div className="border rounded-lg p-4 h-full flex items-center justify-center">
        <div className="animate-pulse text-neutral-400">
          Loading calendar...
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden h-full flex flex-col relative">
      <div className="flex-shrink-0 bg-white shadow-sm">
        <div className="flex">
          {days.map((day, index) => (
            <div
              key={index}
              className="flex-1 text-center font-medium text-xs 2xl:text-[13px] p-2 bg-indigo-50 
                       rounded-xl transition-colors hover:bg-blue-100 m-1"
            >
              <div className="flex flex-row items-center justify-center h-full space-x-1 text-indigo-800">
                <span>{format(day, "EEE d MMM")}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div ref={calendarRef} className="flex-grow overflow-y-auto no-scrollbar">
        <div className="flex h-full">
          {calendarItems.map((day, dateIndex) => (
            <CalendarDay key={dateIndex} activities={day.items} />
          ))}
        </div>
      </div>

      <div className="absolute bottom-4 right-4 flex space-x-2">
        <button
          onClick={onPrevBlock}
          className="px-2.5 py-0.5 rounded-full bg-white border shadow-sm hover:bg-gray-50 
                   transition-colors duration-200"
          aria-label="Previous days"
        >
          <i className="fa-regular fa-chevron-left text-gray-600 fa-sm" />
        </button>
        <button
          onClick={onNextBlock}
          className="px-2.5 py-0.5 rounded-full bg-white border shadow-sm hover:bg-gray-50 
                   transition-colors duration-200"
          aria-label="Next days"
        >
          <i className="fa-regular fa-chevron-right text-gray-600 fa-sm" />
        </button>
      </div>
    </div>
  );
};

export default Calendar;
